import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { CasesService } from 'src/app/services/cases.service';
import { RxHook, RxState } from 'src/app/shared/RxHook';
import { ICaseFlagStatus, ICaseMessage, ICaseSecuritySummary, ICaseViewModel, IDebtorStats, IHistoryData, IInvoice, ICasePaymentSummary, IUploadDocumentRestrictions, WebUserRight, ICaseViewModelExtended, IClaimSettings } from 'src/app/shared/models';
import { Observable, combineLatest, of } from 'rxjs';
import { shareReplay, mergeMap, map, tap } from 'rxjs/operators';
import { DebtorsService } from 'src/app/services/debtors.service';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { MessagesService } from 'src/app/messages/messages.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { WithdrawCaseComponent } from './withdraw-case.component';
import { CaseOpenLocationService } from '../case-open-location.service';
import { LocalizationService, LocalizationSettings } from 'src/app/shared/localization/localization.service';
import { SettingsService } from 'src/app/services/settings.service';
import { AppService } from 'src/app/services/app.service';
import { FirmService } from 'src/app/shared/firm/firm.service';
import { PauseCaseComponent } from './pause-case/pause-case.component';
import { CommonModule, KeyValue } from '@angular/common';
import { UtilsService } from 'src/app/shared/utils.service';
import { TranslateModule } from '@ngx-translate/core';
import { CreditScoreComponent } from './credit-score/credit-score.component';
import { CaseAddPaymentComponent } from './case-add-payment.component';
import { SecuritiesComponent } from './securities/securities.component';
import { PaymentsComponent } from './payments/payments.component';
import { CaseInvoicesComponent } from './case-invoices.component';
import { CaseRemittanceComponent } from './case-remittance.component';
import { CaseRequirementsComponent } from './case-requirements.component';
import { CaseNotesComponent } from './case-notes.component';
import { CaseTimelineComponent } from './case-timeline.component';
import { CaseCollectionProcessComponent } from './case-collection-process.component';
import { CaseDebtorComponent } from './case-debtor.component';
import { CaseApprovalComponent } from './case-approval.component';
import { CaseScoreComponent } from './case-score/case-score.component';
import { UnfoundedDebtCasePopupComponent } from './unfounded-debt-case/unfounded-debt-case-popup.component';

export type CaseLoadType = undefined | 'notes' | 'invoices' | 'flag';
@Component({
  selector: 'app-case',
  templateUrl: './case.component.html',
  styleUrls: ['./case.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RxHook],
  imports: [CommonModule, TranslateModule, CaseAddPaymentComponent, SecuritiesComponent, PaymentsComponent, CaseInvoicesComponent, CaseScoreComponent,
    CaseRemittanceComponent, CaseRequirementsComponent, CaseNotesComponent, CaseTimelineComponent, CaseCollectionProcessComponent, CaseDebtorComponent, CaseApprovalComponent]
})
export class CaseComponent implements OnChanges, OnInit {

  constructor(private service: CasesService, private rxHook: RxHook, private debtorsService: DebtorsService, private notifications: MessageToUiService
    , private messagesService: MessagesService, private matDialog: MatDialog, private appService: AppService, private firmService: FirmService
    , public openFromTracker: CaseOpenLocationService, private localizationService: LocalizationService, private settingsService: SettingsService
    , public dialog: MatDialog, public utilsService: UtilsService) {
  }

  ngOnInit(): void {
  }


  @Input() caseNumber: number;

  displayWithdrawOption$ = this.appService.userHasRight(WebUserRight.WebCloseCaseLocked);
  displayPaymentOption$ = this.appService.userHasRight(WebUserRight.RegisterPayment);
  approveLegalRequest$ = this.appService.userHasRight(WebUserRight.ApproveLegalRequest); 
  isCancelCaseUnfoundedDebtCollectionEnabled$ = this.settingsService.getSettingAsBoolean('CancelCase_UnfoundedDebtCollection_Enabled');

  notes: RxState<ICaseMessage[]>;
  debtorStats: RxState<IDebtorStats>;
  caseEvents: RxState<IHistoryData[]>;
  invoices: RxState<IInvoice[]>;
  payments: RxState<ICasePaymentSummary[]>;
  securities: RxState<ICaseSecuritySummary[]>;
  caseFlag: RxState<ICaseFlagStatus>;
  approvals: RxState<ICaseMessage[]>;
  case: RxState<ICaseViewModel>;
  localizationSettings: RxState<LocalizationSettings>;
  language: RxState<string>;
  uploadDocumentRestrictions: RxState<IUploadDocumentRestrictions>;
  uploadInvoiceRestrictions: RxState<IUploadDocumentRestrictions>;
  firmClaimSettings: RxState<IClaimSettings>
  remittanceSelected: boolean;
  kreditNoteSelected: boolean;
  caseTransactionsTab: CaseTransactionsTab = 'Invoices';
  hasInternationalCurrency: boolean;
  firmCurrencyCode = this.firmService.currency;

  canPauseCase: RxState<boolean>;
  caseEvents$: Observable<IHistoryData[]>;

  @ViewChild("smsModal") smsModal: TemplateRef<any>;

  ngOnChanges(changes: SimpleChanges): void {
    const caseChange = changes.caseNumber;

    if (caseChange.currentValue != caseChange.previousValue) {
      this.load();
      if (this.caseNumber != null) {
        this.service.addToLastAccessed(this.caseNumber).subscribe();

        this.canPauseCase = this.rxHook.use(combineLatest([this.caseEvents$
          , this.appService.userDaysToDebtCollection()
          , this.appService.userHasRight(WebUserRight.ToDebtCollectionList)]).pipe(map(([caseActions, userDaysToDebtCollection, hasRight]) => {
            if (hasRight == false) {return false; }

            const millisecondsToDebtCollection = userDaysToDebtCollection * 24 * 60 * 60 * 1000;

            return caseActions.some(caseAction => this.qualifiesCaseForDebtCollection(caseAction, millisecondsToDebtCollection))
          })));
      }
    }
  }


  private qualifiesCaseForDebtCollection(action: IHistoryData, millisecondsToDebtCollection: number) {
    if (action?.code?.toLocaleLowerCase() !== "inkove")
      {return false}

    if (action.isExecuted == true)
      {return false; }

    const actionDate = new Date(action.date);
    const now = new Date();

    if (actionDate.getTime() - now.getTime() > millisecondsToDebtCollection)
      {return false; }

    return true;

  }

  toggleCaseFlag(caseNumber: number) {
    this.service.toggleCaseFlagStatus(caseNumber)
      .subscribe(() => this.load('flag'));
  }

  private load(area: CaseLoadType = undefined) {
    const case$ = (this.caseNumber ? this.service.getByNumber(this.caseNumber) : of(null as ICaseViewModelExtended))
      .pipe(shareReplay(1),
        tap(_ => this.hasInternationalCurrency = _?.currencyCode != this.firmService.currency));

    this.caseEvents$ = case$.pipe(mergeMap(_ => this.service.getActions(_.number)
      .pipe(map(_ =>  _.map(a => {
          a.isSms = a.type == 'SMS';
          a.isLetter = a.type == 'Letter';
          a.isEmail = a.type == 'EMAIL';
          return a;
      })
    ))), shareReplay(1));

    const debtorStats$ = case$.pipe(mergeMap(_ => this.debtorsService.getStatistics(_.debtor.id, true)));
    const caseInvoices$ = case$.pipe(mergeMap(_ => this.service.getInvoices(_.number)));
    const casePayments$ = case$.pipe(mergeMap(_ => this.service.getPayments(_.number)));
    const caseSecurities$ = case$.pipe(mergeMap(_ => this.service.getSecurities(_.number, true)));
    const caseNotes$ = case$.pipe(mergeMap(_ => this.messagesService.getCaseMessages(_.number)), shareReplay(1));
    const caseFlag$ = case$.pipe(mergeMap(_ => this.service.getCaseFlagStatus(_.number)));
    const approvals$ = caseNotes$.pipe(map(_ => _.filter(note => note.requiresPermission && !note.isTreated)));
    const localizationSettings$ = this.localizationService.settings$;
    const language$ = this.localizationService.settings$.pipe(map(_ => _.language.toLocaleLowerCase()));
    const uploadDocumentRestrictions$ = this.settingsService.getFileUploadRestrictions();
    const uploadInvoiceRestrictions$ = this.settingsService.getInvoiceFileUploadRestrictions();
    const firmClaimSettings$ = this.firmService.getClaimSettings();

    switch (area) {
      case 'notes': this.notes = this.rxHook.use(caseNotes$); break;
      case 'invoices': this.invoices = this.rxHook.use(caseInvoices$); break;
      case 'flag': this.caseFlag = this.rxHook.use(caseFlag$); break;
      default:
        {
          this.case = this.rxHook.use(case$);
          this.notes = this.rxHook.use(caseNotes$);
          this.debtorStats = this.rxHook.use(debtorStats$);
          this.caseEvents = this.rxHook.use(this.caseEvents$);
          this.invoices = this.rxHook.use(caseInvoices$);
          this.payments = this.rxHook.use(casePayments$);
          this.securities = this.rxHook.use(caseSecurities$);
          this.caseFlag = this.rxHook.use(caseFlag$)
          this.approvals = this.rxHook.use(approvals$);
          this.localizationSettings = this.rxHook.use(localizationSettings$);
          this.language = this.rxHook.use(language$);
          this.uploadDocumentRestrictions = this.rxHook.use(uploadDocumentRestrictions$);
          this.uploadInvoiceRestrictions = this.rxHook.use(uploadInvoiceRestrictions$);
          this.firmClaimSettings = this.rxHook.use(firmClaimSettings$);
          break;
        }
    }
  }

  refresh(area: CaseLoadType = undefined) {
    this.load(area);
  }

  openCaseLetter(event : {identifier: string, encode : boolean}) {
    this.service.downloadLetter(this.case.value?.number, event.identifier)
      .subscribe({
        next: (_) => {
          if (event.encode)
            {_ = this.utilsService.tryEncodeAsUTF8(_); }
          const fileURL = window.URL.createObjectURL(_);
          const tab = window.open();
          tab.location.href = fileURL;
        },
        error: () => this.notifications.genericError()
      });
  }

  openSms(item: IHistoryData) {
    this.service.downloadLetter(this.case.value?.number, item.documentIdentifier)
      .subscribe({
        next: (_) => {
          _.text().then((value) => {
            this.viewSMS(item, value);
          })
            .catch((err) => {
              this.notifications.error(err)
            });
        },
        error: () => this.notifications.genericError()
      });
  }

  openDialog(dialogType: AvailablePopups) {
    
    let windowType; let windowOptions: MatDialogConfig = {}; let windowInputs: KeyValue<string, any>[] = [];

    switch (dialogType) {

      case 'PauseCase':
        windowType = PauseCaseComponent;
        windowOptions = {
          maxWidth: window.innerWidth < 1080 ? "90vw" : "30vw",
          minWidth: window.innerWidth < 1080 ? "90vw" : "unset"
        };
        windowInputs = [{ key: "case", value: this.case }]
        break;

      case 'WithdrawCase':
        windowType = WithdrawCaseComponent;
        windowInputs = [{ key: "caseNumber", value: this.caseNumber }]
        break;

      case 'UnfoundedDebt':
        windowType = UnfoundedDebtCasePopupComponent;
        windowInputs = [{ key: "caseNumber", value: this.caseNumber }]
        break;

      default:
        return;
    }

    const dialogRef = this.matDialog.open(windowType, windowOptions);

    windowInputs.forEach(input => {
      dialogRef.componentInstance[input.key] = input.value;
    });

    dialogRef.afterClosed().subscribe(changes => { if (changes === true) {this.refresh()} })
  }

  viewSMS(smsItem: IHistoryData, content: string) {
    this.dialog.open(this.smsModal, {
      data: {
        message: smsItem,
        content
      }
    });
  }
}

export type CaseTransactionsTab = 'Invoices' | 'Payments' | 'Securities';
export type AvailablePopups = 'PauseCase' | 'WithdrawCase' | 'UnfoundedDebt';
